import React from 'react';
import { TagDisplay } from 'oolib/dist/v2/components/Tags';
import { colors2, getText, getVal, icons, SANS_2, SANS_3, TooltipV2, UI_BODY_SM_DF, UI_CAPTION_SEMIBOLD_DF } from 'oolib';

/**
 * leadingIcons: optional array of indicators rendered BEFORE the primary text.
 * Each entry renders an icon when the row's value at valuePath is truthy.
 *
 * Rendering modes (auto-detected from the valuePath value):
 *  - positive number  → colored pill with icon + count (icon-color bg,
 *                       white fg by default — override via badgeFgColor)
 *  - other truthy     → bare icon (legacy single-glyph behavior)
 *  - falsy / 0        → nothing
 *
 * Shape:
 *   {
 *     valuePath: string,          // row data path; truthy => show
 *     tooltip?: string,
 *     icon: {
 *       name: string,             // phosphor icon name (key on `icons` from oolib)
 *       color?: string,           // icon fill (bare mode) / pill foreground (pill mode); defaults to colors2.grey80
 *       size?: number,            // defaults to 16
 *       weight?: string,          // phosphor weight; defaults to 'fill'
 *     },
 *     badgeBgColor?: string,      // pill background; defaults to icon.color
 *     badgeFgColor?: string,      // pill foreground override; defaults to icon.color
 *   }
 */
const TableTextWithTag = ({
  value: _value,
  tableRowData,
  typoComp = 'UI_BODY_SM_DF',
  tagValuePath,
  labelValuePath,
  colorPreset = 'warning',
  leadingIcons,
  ...typoCompProps
}) => {
  const tagText = tagValuePath
    ? getVal(tableRowData, tagValuePath)
    : undefined;

  const tagLabel = labelValuePath
    ? getVal(tableRowData, labelValuePath)
    : undefined;

  const visibleLeadingIcons = (leadingIcons || []).filter(
    (li) => li?.valuePath && !!getVal(tableRowData, li.valuePath)
  );

  if (
    (!_value || (_value.blocks && _value.blocks.length === 0)) &&
    !tagText &&
    visibleLeadingIcons.length === 0
  )
    return null;

  const comps = { SANS_2, SANS_3, UI_BODY_SM_DF };
  const TYPO_COMP = comps[typoComp];
  const primaryText = _value ? getText(_value) : '';

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '0.5rem',
        width: '100%',
        minWidth: 0,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          flex: '1 1 auto',
          minWidth: 0,
        }}
      >
        {visibleLeadingIcons.map((li, idx) => {
          const IconComp = li.icon?.name ? icons[li.icon.name] : null;
          if (!IconComp) return null;
          // Single source of truth: the same valuePath value gates visibility
          // (already filtered above) AND drives the badge when numeric.
          const rawVal = getVal(tableRowData, li.valuePath);
          const badgeNum = Number(rawVal);
          const showBadge = Number.isFinite(badgeNum) && badgeNum > 0;

          const iconColor = li.icon.color || colors2.grey80;
          const iconSize = li.icon.size || 16;
          const iconWeight = li.icon.weight || 'fill';

          // When the icon carries a badge we wrap the pair in a colored pill
          // so the indicator reads as one tappable badge instead of two
          // adjacent glyphs. Bare-icon rendering is unchanged for configs
          // whose valuePath value isn't a positive number.
          const pillFg = li.badgeFgColor || iconColor;
          const pillBg = li.badgeBgColor || iconColor;
          const inner = showBadge ? (
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.2rem',
                padding: '1px 6px',
                borderRadius: 999,
                backgroundColor: pillBg,
                color: pillFg,
                lineHeight: 1,
                flex: '0 0 auto',
              }}
            >
              <IconComp
                size={iconSize}
                color={pillFg}
                weight={iconWeight}
                style={{ display: 'block', flex: '0 0 auto' }}
              />
              <UI_CAPTION_SEMIBOLD_DF
                style={{
                  color: pillFg,
                  lineHeight: 1,
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {badgeNum}
              </UI_CAPTION_SEMIBOLD_DF>
            </span>
          ) : (
            <IconComp
              size={iconSize}
              color={iconColor}
              weight={iconWeight}
              style={{ display: 'block', flex: '0 0 auto' }}
            />
          );

          return (
            <TooltipV2
              key={li.valuePath || idx}
              text={li.tooltip || ''}
              showPointer={true}
              placement='top'
            >
              {inner}
            </TooltipV2>
          );
        })}
        <TYPO_COMP
          {...(typoCompProps || {})}
          style={{
            flex: '1 1 auto',
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {primaryText || null}
        </TYPO_COMP>
      </div>
      {tagLabel ? (
        <TooltipV2 text={tagText} showPointer={true} placement='right'>
           <TagDisplay display={tagLabel} colorPreset={colorPreset}/>
          </TooltipV2>
      ) : null}
    </div>
  );
};

export default TableTextWithTag;
